import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { InputLabel } from '@wajeez-web/components/ui';
import { UploadFileFormType } from '../Types';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { useEffectCancelled } from '@wajeez-web/components/helpers';
import { MediaHelpers } from '@wajeez-web/core/api';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';

export interface FormFileDragAndDropInputProps<TFormFields extends FieldValues>
  extends UseControllerProps<TFormFields> {
  fieldType: UploadFileFormType;
  disabled?: boolean;
}

const FormFileDragAndDropInput = <TFormFields extends FieldValues>({
  fieldType,
  disabled,
  ...props
}: FormFileDragAndDropInputProps<TFormFields>) => {
  const { field, fieldState } = useController({
    ...props,
    rules: {
      required: fieldType.required ? 'Required' : undefined,

      ...props.rules,
    },
  });

  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const [src, setSrc] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [isDragging, setDragging] = useState<boolean>(false);

  const { lang } = useCommonSelector((state) => state.common.settings);

  useEffect(() => {
    if (field.value) {
      MediaHelpers.getMediaUrlByIdAsync(field.value)
        .then((res) => {
          setSrc(res);
        })
        .catch(() => {
          setSrc('');
        });
    }
  }, [field.value]);

  const uploadFile = async (e: any) => {
    const file = e.target.files?.[0];

    if (file) {
      setIsLoading(true);
      try {
        const res = await MediaHelpers.uploadMedia({
          data: file,
          fileName: file.name,
        });
        field.onChange(res);
        field.onBlur();
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    }
  };

  // This function will be triggered when dropping
  const dropHandler = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isDragging) {
      setDragging(false);
    }
    const file = event?.dataTransfer?.files[0];

    if (file) {
      setIsLoading(true);
      try {
        const res = await MediaHelpers.uploadMedia({
          data: file,
          fileName: file.name,
        });
        field.onChange(res);
        field.onBlur();
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    }
  };

  // This makes the third box become droppable
  const allowDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isDragging) {
      setDragging(true);
    }
  };

  const isError = fieldState.error && fieldState.isTouched;
  return (
    <div>
      {fieldType.label && (
        <InputLabel
          text={
            t(
              `inputs.labels.${fieldType.label}`,
              fieldType.label.toString() || ''
            ) as string
          }
          name={field.name}
          required={fieldType.required}
          hint={fieldType.span}
        />
      )}{' '}
      {isLoading ? (
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <div
          onClick={() => {
            inputRef.current?.click();
          }}
          onDragOver={allowDrop}
          onDrop={dropHandler}
          className={`mt-1 cursor-pointer flex justify-center px-6 pt-5 pb-6 border-2 ${
            isError ? 'border-red-500' : ''
          } ${
            isDragging ? 'border-primary' : 'border-gray-300'
          } hover:border-primary border-dashed rounded-md`}
        >
          <div className="space-y-1 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="flex text-sm text-gray-600">
              <label
                htmlFor="file-upload"
                className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-light focus-within:outline-none "
              >
                {src == '' && (
                  <span>
                    <ReturnTranslatedText
                      origintext="Upload a file"
                      text={`inputs.placeholders.${'Upload a file'}`}
                    />
                    &nbsp;
                  </span>
                )}
                <input
                  // name={name}
                  ref={inputRef}
                  id="file-upload"
                  onChange={uploadFile}
                  type="file"
                  className="sr-only"
                />
              </label>
              {src == '' && (
                <p className="pl-1">
                  <ReturnTranslatedText
                    origintext="or drag and drop"
                    text={`inputs.placeholders.${'or drag and drop'}`}
                  />
                </p>
              )}
            </div>
            <div className="text-sm text-gray-600">
              {src !== '' &&
                (lang === 'en' ? (
                  <p>{`${1} ${'file has'} been uploaded`}</p>
                ) : (
                  <p>{`تم رفع ${1} ${'ملف'}`}</p>
                ))}
            </div>
            <p className="text-xs justify-center flex flex-row text-gray-500">
              {fieldType.fieldOptions.types?.join(' ,')}{' '}
              {fieldType.fieldOptions.upToKB &&
                'up to ' + fieldType.fieldOptions.upToKB}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormFileDragAndDropInput;
